import {
  CommunicationClientSearchParameters,
  CommunicationClientSearchResponse,
  CommunicationCreateData,
  CommunicationDeliveryPageResponse,
  CommunicationResponsePageResponse,
  CommunicationSearchOptions,
  CommunicationSearchParameters,
  CommunicationSearchResponse,
  DeliverySearchParameters,
} from "@/types/communication";
import { apiUrl } from "../../utils/url";
import { HttpClient, HttpError } from "../HttpClient";
import { getErrorMessage } from "../errors";
import { ICommunicationService } from "./ICommunicationService";

export class CommunicationService implements ICommunicationService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = new HttpClient({
      baseURL: apiUrl,
      timeout: 10000,
      headers: {},
    });
  }

  async create(communicationData: CommunicationCreateData): Promise<number> {
    try {
      return await this.httpClient.post<number>(
        `/communication`,
        communicationData,
        {},
        true,
      );
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível criar a comunicação. Confirme o animal, o modelo e o conteúdo.",
      );
      throw new Error(errorMessage);
    }
  }

  /**
   * Clients reachable by a communication, narrowed by the audience filters.
   *
   * The response is a paged envelope whose items the OpenAPI document leaves
   * untyped, so it is returned raw and normalized by the caller's adapter.
   */
  async searchClients(
    parameters: CommunicationClientSearchParameters,
    options: CommunicationSearchOptions = {},
  ): Promise<CommunicationClientSearchResponse> {
    const { page = 0, size = 10, sort, signal } = options;

    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
      });
      if (sort) queryParams.set("sort", sort);

      return await this.httpClient.post<CommunicationClientSearchResponse>(
        `/communication/search-client-communication?${queryParams.toString()}`,
        parameters,
        { signal },
      );
    } catch (error) {
      if (error instanceof HttpError && error.status === 404) {
        return {
          content: [],
          page: { size, number: page, totalElements: 0, totalPages: 0 },
        };
      }

      if (error instanceof DOMException && error.name === "AbortError") {
        throw error;
      }

      const errorMessage = getErrorMessage(
        error,
        "Não foi possível calcular os destinatários. Reveja os filtros e tente novamente.",
      );
      throw new Error(errorMessage);
    }
  }

  async search(
    parameters: CommunicationSearchParameters,
    options: CommunicationSearchOptions = {},
  ): Promise<CommunicationSearchResponse> {
    const { page = 0, size = 10, sort, signal } = options;

    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
      });
      if (sort) queryParams.set("sort", sort);

      return await this.httpClient.post<CommunicationSearchResponse>(
        `/communication/search?${queryParams.toString()}`,
        parameters,
        { signal },
      );
    } catch (error) {
      if (error instanceof HttpError && error.status === 404) {
        return {
          content: [],
          page: { size, number: page, totalElements: 0, totalPages: 0 },
        };
      }

      if (error instanceof DOMException && error.name === "AbortError") {
        throw error;
      }

      /* Shared by the Histórico tab and the attendee history modal, so the
         copy names neither. */
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível carregar as comunicações. Atualize a página e tente novamente.",
      );
      throw new Error(errorMessage);
    }
  }

  async searchDeliveries(
    communicationId: number,
    parameters: DeliverySearchParameters = {},
    options: CommunicationSearchOptions = {},
  ): Promise<CommunicationDeliveryPageResponse> {
    const { page = 0, size = 10, sort, signal } = options;

    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
      });
      if (sort) queryParams.set("sort", sort);
      /* Omitted when unset; every filter is optional. */
      if (parameters.statusCode) {
        queryParams.set("statusCode", parameters.statusCode);
      }
      if (parameters.recipientSearch) {
        queryParams.set("recipientSearch", parameters.recipientSearch);
      }
      /* The API hides resend deliveries unless asked, so only `true` is sent. */
      if (parameters.includeResends) {
        queryParams.set("includeResends", "true");
      }

      return await this.httpClient.get<CommunicationDeliveryPageResponse>(
        `/communication/${communicationId}/delivery?${queryParams.toString()}`,
        { signal },
      );
    } catch (error) {
      if (error instanceof HttpError && error.status === 404) {
        return {
          content: [],
          page: { size, number: page, totalElements: 0, totalPages: 0 },
        };
      }

      if (error instanceof DOMException && error.name === "AbortError") {
        throw error;
      }

      const errorMessage = getErrorMessage(
        error,
        "Não foi possível carregar os destinatários desta comunicação. Tente novamente.",
      );
      throw new Error(errorMessage);
    }
  }

  async searchResponses(
    communicationId: number,
    options: CommunicationSearchOptions = {},
  ): Promise<CommunicationResponsePageResponse> {
    const { page = 0, size = 10, sort, signal } = options;

    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
      });
      if (sort) queryParams.set("sort", sort);

      return await this.httpClient.get<CommunicationResponsePageResponse>(
        `/communication/${communicationId}/response?${queryParams.toString()}`,
        { signal },
      );
    } catch (error) {
      if (error instanceof HttpError && error.status === 404) {
        return {
          content: [],
          page: { size, number: page, totalElements: 0, totalPages: 0 },
        };
      }

      if (error instanceof DOMException && error.name === "AbortError") {
        throw error;
      }

      const errorMessage = getErrorMessage(
        error,
        "Não foi possível carregar as respostas desta comunicação. Tente novamente.",
      );
      throw new Error(errorMessage);
    }
  }

  async getDeliveryStatusCodes(): Promise<string[]> {
    try {
      return await this.httpClient.get<string[]>(
        `/communication/delivery/status-codes`,
      );
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível carregar os estados de entrega.",
      );
      throw new Error(errorMessage);
    }
  }

  async getStatusCodes(): Promise<string[]> {
    try {
      return await this.httpClient.get<string[]>(`/communication/status-codes`);
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível carregar os estados das comunicações.",
      );
      throw new Error(errorMessage);
    }
  }

  async getDeliveryErrorCodes(): Promise<string[]> {
    try {
      return await this.httpClient.get<string[]>(
        `/communication/delivery/error-codes`,
      );
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível carregar os motivos de falha das entregas.",
      );
      throw new Error(errorMessage);
    }
  }
}
